import type { MatchRule } from '@filmsloth/shared';
import { useT } from '../i18n/index.js';

interface MatchRuleSettingProps {
  rule: MatchRule;
  canEdit: boolean;
  onChange: (rule: MatchRule) => void;
}

const RULES: MatchRule[] = ['lenient', 'strict'];

/**
 * How agreeable a film has to be before the app calls it.
 *
 * Two options rather than a slider: "nobody minds" and "everybody wants it"
 * are the two answers a group actually gives, and anything in between would
 * need a sentence of explanation per step.
 */
export function MatchRuleSetting({ rule, canEdit, onChange }: MatchRuleSettingProps) {
  const t = useT();
  const hint = rule === 'strict' ? t('matchRule.strictHint') : t('matchRule.lenientHint');

  if (!canEdit) {
    return (
      <span className="muted small">
        {rule === 'strict' ? t('matchRule.strict') : t('matchRule.lenient')} · {hint}
      </span>
    );
  }

  return (
    <div className="stack stack--tight">
      <div className="chips" role="group" aria-label={t('matchRule.label')}>
        {RULES.map((option) => (
          <button
            key={option}
            type="button"
            className={`chip${option === rule ? ' chip--selected' : ''}`}
            aria-pressed={option === rule}
            onClick={() => {
              if (option !== rule) onChange(option);
            }}
          >
            {option === 'strict' ? t('matchRule.strict') : t('matchRule.lenient')}
          </button>
        ))}
      </div>
      <span className="muted small">{hint}</span>
    </div>
  );
}
